import React, { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/context/AuthContext';
import { ScoreCard } from '@/components/ScoreCard';
import { ScoreRadar } from '@/components/WindCharts';
import { Seo } from '@/components/Seo';
import { Footer } from '@/components/Footer';
import { Button } from '@/components/ui/button';
import {
  Wind, MapPin, Clapperboard, Sparkles, ChevronRight, Trophy, Play, FlaskConical, ArrowRight,
} from 'lucide-react';

const fmt = (s) => { const m = Math.floor(s / 60); const sec = Math.floor(s % 60); return `${m}:${sec.toString().padStart(2, '0')}`; };

// Demo data — numeric facts live here, narrative copy comes from i18n (example.*)
// so the example is fully bilingual and follows the active site language.
const SCORES = { body_position: 7, kite_control: 8, board_edge: 6, timing: 6, style: 7 };
const TRICK_META = [
  { timestamp_s: 1.5, score: 7 },
  { timestamp_s: 4.0, score: 6 },
  { timestamp_s: 6.3, score: 6 },
];
const TIP_PRIORITY = [1, 2, 3];
const SAMPLE_VIDEO = '/sample/kitesurf-example.mp4';
const SAMPLE_POSTER = '/sample/kitesurf-example-poster.jpg';

export default function ExampleAnalysis() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();
  const videoRef = useRef(null);

  const tricksCopy = t('example.tricks', { returnObjects: true });
  const observations = t('example.observations', { returnObjects: true });
  const tipsCopy = t('example.tips', { returnObjects: true });
  const tricks = Array.isArray(tricksCopy) ? tricksCopy.map((tc, i) => ({ ...tc, ...TRICK_META[i] })) : [];
  const obs = Array.isArray(observations) ? observations : [];
  const tips = Array.isArray(tipsCopy) ? tipsCopy.map((tc, i) => ({ ...tc, priority: TIP_PRIORITY[i] })) : [];

  const overall = (Object.values(SCORES).reduce((p, c) => p + c, 0) / Object.values(SCORES).length).toFixed(1);
  const ctaTarget = user ? '/analyze' : '/register';

  const seek = (ts) => {
    if (videoRef.current) {
      videoRef.current.currentTime = ts;
      videoRef.current.play().catch(() => {});
      videoRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  return (
    <div className="min-h-screen">
      <Seo title={t('example.bannerTitle')} description={t('example.bannerDesc')} />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 py-8" data-testid="example-analysis">
        {/* Example banner */}
        <div
          className="rounded-2xl p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center gap-4 justify-between"
          style={{ background: 'radial-gradient(70% 120% at 0% 0%, rgba(255,107,53,0.14), rgba(255,107,53,0)), #12161B', border: '1px solid rgba(255,107,53,0.28)' }}
          data-testid="example-banner"
        >
          <div className="flex items-start gap-3">
            <div className="w-11 h-11 rounded-xl bg-[#FF6B35]/14 flex items-center justify-center shrink-0">
              <FlaskConical className="w-5 h-5 text-[#FF6B35]" />
            </div>
            <div>
              <div className="label-eyebrow text-[#FF6B35] mb-1">{t('example.badge')}</div>
              <h2 className="font-display text-2xl sm:text-3xl leading-tight">{t('example.bannerTitle')}</h2>
              <p className="text-sm text-muted-foreground mt-1 max-w-2xl">{t('example.bannerDesc')}</p>
            </div>
          </div>
          <Button
            onClick={() => navigate(ctaTarget)}
            className="bg-[#00B4D8] hover:bg-[#48CAE4] text-black font-semibold rounded-xl shrink-0"
            data-testid="example-banner-cta"
          >
            <Sparkles className="w-4 h-4 mr-2" /> {t('example.cta')}
          </Button>
        </div>

        {/* header */}
        <div className="flex items-start justify-between gap-4 flex-wrap mt-8">
          <div>
            <div className="flex items-center gap-2 label-eyebrow text-[#48CAE4] mb-2"><Clapperboard className="w-4 h-4" /> {t('analysis.coachingReport')}</div>
            <h1 className="font-display text-4xl sm:text-5xl">{t('example.sessionTitle')}</h1>
            <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground flex-wrap">
              <span className="flex items-center gap-1"><MapPin className="w-4 h-4" /> {t('example.metaSpot')}</span>
              <span className="flex items-center gap-1"><Wind className="w-4 h-4" /> 22 kn {t('example.windDir')}</span>
              <span className="font-data">{fmt(8)}</span>
            </div>
          </div>
          <div className="text-right">
            <div className="label-eyebrow text-muted-foreground">{t('analysis.overall')}</div>
            <div className="font-data text-4xl font-semibold text-[#00B4D8]" data-testid="example-overall-score">{overall}<span className="text-base text-muted-foreground">/10</span></div>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-6 mt-8">
          {/* LEFT: video + summary + tricks + observations */}
          <div className="lg:col-span-2 space-y-6">
            <div className="kite-card overflow-hidden">
              <video
                ref={videoRef}
                src={SAMPLE_VIDEO}
                poster={SAMPLE_POSTER}
                controls
                playsInline
                preload="metadata"
                className="w-full aspect-video bg-black"
                data-testid="example-video"
              />
            </div>

            <div className="kite-card p-5">
              <div className="label-eyebrow text-muted-foreground mb-2">{t('analysis.sessionSummary')}</div>
              <p className="text-lg leading-relaxed">{t('example.summary')}</p>
            </div>

            {/* tricks */}
            <div className="kite-card p-5">
              <h3 className="font-semibold flex items-center gap-2 mb-3"><Sparkles className="w-4 h-4 text-[#48CAE4]" /> {t('analysis.detectedTricks')}</h3>
              <div className="space-y-2">
                {tricks.map((tr, i) => (
                  <button
                    key={i}
                    onClick={() => seek(tr.timestamp_s)}
                    data-testid="example-trick-timestamp-button"
                    className="w-full flex items-center gap-3 p-3 rounded-xl bg-[#0A0A0A] border border-white/8 hover:border-[#48CAE4]/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#48CAE4]/50 transition-colors text-left group"
                  >
                    <span className="font-data text-xs px-2 py-1 rounded-md bg-[#00B4D8]/12 text-[#00B4D8]">{fmt(tr.timestamp_s)}</span>
                    <div className="flex-1">
                      <div className="font-medium">{tr.name}</div>
                      {tr.notes && <div className="text-xs text-muted-foreground">{tr.notes}</div>}
                    </div>
                    <span className="font-data text-sm text-[#48CAE4]">{tr.score}/10</span>
                    <Play className="w-4 h-4 text-muted-foreground group-hover:text-[#48CAE4]" />
                  </button>
                ))}
              </div>
            </div>

            {/* observations */}
            {obs.length > 0 && (
              <div className="kite-card p-5">
                <h3 className="font-semibold mb-3">{t('analysis.whatAiSaw')}</h3>
                <ul className="space-y-2">
                  {obs.map((o, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-white/85"><ChevronRight className="w-4 h-4 text-[#48CAE4] mt-0.5 shrink-0" /> {o}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* RIGHT: scores + tips + encouragement + CTA */}
          <div className="space-y-6">
            <div className="kite-card p-5">
              <h3 className="font-semibold mb-2">{t('analysis.techniqueScores')}</h3>
              <ScoreRadar scores={SCORES} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              {Object.entries(SCORES).map(([k, v]) => <ScoreCard key={k} k={k} value={v} />)}
            </div>

            {/* tips */}
            <div className="kite-card p-5">
              <h3 className="font-semibold flex items-center gap-2 mb-3"><Trophy className="w-4 h-4 text-[#FF6B35]" /> {t('analysis.topDrills')}</h3>
              <div className="space-y-3">
                {tips.map((tip, i) => (
                  <div key={i} className="p-3 rounded-xl bg-[#0A0A0A] border border-white/8">
                    <div className="flex items-center gap-2">
                      <span className="w-6 h-6 rounded-full bg-[#00B4D8] text-black font-data text-xs font-bold flex items-center justify-center">{tip.priority}</span>
                      <span className="font-medium text-sm">{tip.tip}</span>
                    </div>
                    {tip.drill && <p className="text-xs text-muted-foreground mt-2 pl-8"><span className="text-[#48CAE4]">{t('analysis.drill')}</span> {tip.drill}</p>}
                  </div>
                ))}
              </div>
            </div>

            {/* encouragement */}
            <div className="rounded-2xl p-5" style={{ background: 'radial-gradient(60% 80% at 20% 10%, rgba(0,180,216,0.16), rgba(0,180,216,0)), #12161B', border: '1px solid rgba(0,180,216,0.25)' }}>
              <p className="text-[#48CAE4] font-medium italic">“{t('example.encouragement')}”</p>
            </div>

            {/* CTA */}
            <div className="kite-card p-6 text-center" data-testid="example-cta-card">
              <h3 className="font-display text-2xl">{t('example.ctaTitle')}</h3>
              <p className="text-sm text-muted-foreground mt-2">{t('example.ctaDesc')}</p>
              <Button
                onClick={() => navigate(ctaTarget)}
                className="w-full mt-4 bg-[#00B4D8] hover:bg-[#48CAE4] text-black font-semibold rounded-xl"
                data-testid="example-cta-button"
              >
                {t('example.cta')} <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
